import Symbol from "./Symbol";
import SymbolAnim from "./SymbolAnim";
import * as particles from "pixi-particles";
import {pixiSetTimeout} from "../../framework/src/engine/pixi/pixiTimeout";

export default class SymbolStar extends Symbol{
    constructor(commander, speed) {
        super(commander, speed);
    }

    init() {
        this.texture = PIXI.Texture.from("symbol_Star_particle.png");
        this._configTiny = winEmitterConfigTiny;
        this.length = 1050;
    }

    animate(symbol, loop) {
        this.hideSymbol(symbol);
        return new Promise (res => {
            this.start(symbol).then((values) => { // duration - 1050ms
                let bg = values[0];
                let flares = values[1];
                let shine = values[2];
                this.loopShine(loop, shine).then((shine) => { // duration - 500ms * i
                    this.hideBg(bg, flares);
                    this.end(symbol, shine).then(() => res()); // duration - 250ms
                });
            })
        }).then(() => {
            this.clearEmitters();
        });
    }

    start(symbol) {
        return new Promise (res => {
            let promises = [];
            promises.push(this.background(symbol));
            promises.push(this.flares(symbol));
            promises.push(new Promise(r => {
                this.startShine(symbol).then((shine)=>{
                    this.loopShine(1,shine).then((shine) => {
                        r(shine);
                    });
                });
            }));

            promises.push(this.startParticles(symbol));
            promises.push(this.overlay(symbol));

            Promise.all(promises).then((values) => {
                res(values);
            });
        });
    }

    startShine(symbol) {
        return new Promise(res => {
            let shine = SymbolAnim.createSprite(symbol.x, symbol.y, "symbol_Star_shine.png");
            shine.tint = 0xEEEEEE;
            this.layer.addChild(shine);

            createjs.Tween.get(shine.scale)
                .to({x: 0.95, y: 0.95}, 150 * this.speed, createjs.Ease.linear)
                .to({x: 1.05, y: 1.05}, 150 * this.speed, createjs.Ease.linear)
                .to({x: 1.025, y: 1.025}, 400 * this.speed, createjs.Ease.linear)
                .to({x: 1.05, y: 1.05}, 300 * this.speed, createjs.Ease.quadOut)
                .call(() => {
                    SymbolAnim.removeTween(shine.scale);
                    res(shine);
                });
        });
    }

    loopShine(i,shine) {
        return new Promise(res => {
            if (i === 0) {
                res(shine);
            } else {
                createjs.Tween.get(shine.scale,{loop: true})
                    .to({x: 1.025, y: 1.025}, 300 * this.speed, createjs.Ease.linear)
                    .to({x: 1.05, y: 1.05}, 200 * this.speed, createjs.Ease.quadOut)
                    .call(() => {
                        if (--i < 1) {
                            SymbolAnim.removeTween(shine.scale);
                            res(shine);
                        }
                    });

            }
        })
    }

    overlay(symbol) {
        return new Promise(res => {
            let overlay = SymbolAnim.createSprite(symbol.x, symbol.y, "symbol_Star_overlay.png");
            overlay.alpha = 0;
            this.layer.addChild(overlay);

            createjs.Tween.get(overlay.scale)
                .to({x: 0.95, y: 0.95}, 150 * this.speed, createjs.Ease.linear)
                .to({x: 1.05, y: 1.05}, 150 * this.speed, createjs.Ease.linear)
                .to({x: 1.025, y: 1.025}, 400 * this.speed, createjs.Ease.linear)
                .to({x: 1.05, y: 1.05}, 300 * this.speed, createjs.Ease.quadOut)
                .call(() => {
                    SymbolAnim.removeTween(overlay.scale);
                    res();
                });

            createjs.Tween.get(overlay)
                .to({alpha: 0.75}, 400*this.speed, createjs.Ease.circIn)
                .to({alpha: 0}, 200*this.speed, createjs.Ease.circIn)
                .call(() => {
                    SymbolAnim.removeTween(overlay);
                    res();
                });
        })
    }

    background(symbol) {
        return new Promise(res => {
            let bg = SymbolAnim.createSprite(symbol.x, symbol.y, "symbol_Star_background.png");
            bg.alpha = 0;
            this.layer.addChild(bg);

            createjs.Tween.get(bg)
                .wait(50*this.speed)
                .to({alpha: 1}, 150*this.speed, createjs.Ease.quadOut)
                .call(() => {SymbolAnim.removeTween(bg)});

            pixiSetTimeout((this.length / 1000) * this.speed, () => {
                res(bg);
            });
        })
    }

    flares(symbol) {
        return new Promise(res => {
            let flares = SymbolAnim.createSprite(symbol.x, symbol.y, "symbol_Star_flares.png", {x: 0.5, y: 0.48});
            flares.alpha = 0;

            this.layer.addChild(flares);
            createjs.Tween.get(flares)
                .wait(50 * this.speed)
                .to({alpha: 1}, 50 * this.speed, createjs.Ease.quadOut);

            createjs.Tween.get(flares, {loop: true})
                .to({rotation: 10*3.14}, 10*1000* this.speed, createjs.Ease.linear);

            pixiSetTimeout((this.length / 1000) * this.speed, () => {
                res(flares);
            });
        });
    }

    startParticles(symbol) {
        return new Promise(res => {
            let pos = [
                {x: symbol.x,    y: symbol.y-80},
                {x: symbol.x,    y: symbol.y+80},
                {x: symbol.x-80, y: symbol.y},
                {x: symbol.x+80, y: symbol.y},
                {x: symbol.x-30, y: symbol.y+30},
                {x: symbol.x-30, y: symbol.y-30},
                {x: symbol.x+30, y: symbol.y+30},
                {x: symbol.x+30, y: symbol.y-30},
                {x: symbol.x, y: symbol.y}
            ];
            createjs.Tween.get()
                .wait(1000*this.speed)
                .call(() => {
                    this.createSpark(pos[0].x, pos[0].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[0].x, pos[0].y));
                    this.createSpark(pos[1].x, pos[1].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[1].x, pos[1].y));
                    this.createSpark(pos[2].x, pos[2].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[2].x, pos[2].y));
                    this.createSpark(pos[3].x, pos[3].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[3].x, pos[3].y));
                })
                .wait(250*this.speed)
                .call(() => {
                    this.createSpark(pos[4].x, pos[4].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[4].x, pos[4].y));
                    this.createSpark(pos[5].x, pos[5].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[5].x, pos[5].y));
                    this.createSpark(pos[6].x, pos[6].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[6].x, pos[6].y));
                    this.createSpark(pos[7].x, pos[7].y);
                    SymbolAnim.runEmitter(this.createTinyEmitter(pos[7].x, pos[7].y));
                })
                .wait(250*this.speed)
                .call(() => {
                    this.createSpark(pos[8].x, pos[8].y);
                    SymbolAnim.runEmitter(this.createSmallEmitter(pos[8].x, pos[8].y));
                })
                .wait(250*this.speed)
                .call(() => {
                    res();
                });

        })
    }

    createTinyEmitter(x, y) {
        let emitter = new particles.Emitter(   this.layer, this.texture , this._configTiny);
        emitter.updateSpawnPos(x, y);
        this.emitters.push(emitter);
        return emitter;
    }

    end(symbol, shine) {
        return new Promise (res => {
            this.endShine(shine, symbol, res);
        });
    }

    endShine (shine, symbol, callback = () => {}) {
        createjs.Tween.get(shine)
            .wait(350* this.speed)
            .call(() => {
                this.showSymbol(symbol);
            })
            .to({alpha: 0}, 100 * this.speed, createjs.Ease.quadOut)
            .call(() => {
                SymbolAnim.removeTween(shine);
            });
        SymbolAnim.removeTween(shine.scale);
        createjs.Tween.get(shine.scale)
            .to({x: 0.95, y: 0.95}, 100 * this.speed, createjs.Ease.quadOut)
            .to({x: 1, y: 1}, 350 * this.speed, createjs.Ease.quadOut)
            .call(() => {
                SymbolAnim.removeTween(shine.scale);
                callback();
            });
    }

    hideBg(bg, flares) {
        createjs.Tween.get(bg)
            .wait(150*this.speed)
            .to({alpha: 0}, 150 *this.speed, createjs.Ease.linear)
            .call(() => {
                SymbolAnim.removeTween(bg);
            });
        createjs.Tween.get(flares)
            .wait(150*this.speed)
            .to({alpha: 0}, 150 *this.speed, createjs.Ease.linear)
            .call(() => {
                SymbolAnim.removeTween(flares);
            });
    }
}

let winEmitterConfigTiny = {
    "alpha": {
        "start": 1,
        "end": 1
    },
    "scale": {
        "start": 0.8,
        "end": 0.5,
        "minimumScaleMultiplier": 1
    },
    "color": {
        "start": "#ffffff",
        "end": "#ffffff"
    },
    "speed": {
        "start": 150,
        "end": 50,
        "minimumSpeedMultiplier": 1
    },
    "acceleration": {
        "x": 0,
        "y": 0
    },
    "maxSpeed": 0,
    "startRotation": {
        "min": 0,
        "max": 0
    },
    "noRotation": false,
    "rotationSpeed": {
        "min": 0,
        "max": 0
    },
    "lifetime": {
        "min": 0.2,
        "max": 0.3
    },
    "blendMode": "normal",
    "frequency": 0.01,
    "emitterLifetime": 0.17,
    "maxParticles": 75,
    "pos": {
        "x": 0,
        "y": 0
    },
    "addAtBack": false,
    "spawnType": "ring",
    "spawnCircle": {
        "x": 0,
        "y": 0,
        "r": 20,
        "minR": 20
    }
};